import type { ExtractResponseType } from "@hyper-fetch/core";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useMemo, useState } from "react";
import type { CSSProperties } from "react";

import type { sdk } from "@/api/sdk";
import { GoLink, Section, SectionHead, ghostButton } from "@/features/portal/components/portal-ui";
import { CoachPhoto } from "@/features/site/components/coach-photo";
import { SlotGateDialog } from "@/features/site/modules/open-calendar/slot-gate-dialog";
import { CoachRow, dayLabel, row } from "@/features/site/modules/open-calendar/week-grid";
import type { PublicDay, PublicSpecialist, SlotPick } from "@/features/site/types";
import { openTimeLabelIn } from "@/features/site/utils/labels";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import type { ServiceType } from "@complete-athlete/enums";

export type BookableTrainer = ExtractResponseType<typeof sdk.t.bookable.$get>[number];


/** People shown as cards before the rest wait behind "show every time this week". */
const VISIBLE = 4;

/** One person who can be booked, with the trainer whose business takes the booking. */
export type BookablePerson = {
  trainer: BookableTrainer;
  specialist: PublicSpecialist;
  serviceName: string | null;
  /** The catalogue types of what they run, for narrowing people by category. */
  serviceTypes: ServiceType[];
};

/** `count` day keys from `from`, for a person the availability answer has no card for. */
const emptyDays = (from: string, count: number): PublicDay[] =>
  Array.from({ length: count }, (_, index) => {
    const day = new Date(`${from}T12:00:00Z`);
    day.setUTCDate(day.getUTCDate() + index);
    return { date: day.toISOString().slice(0, 10), slots: [] };
  });

/**
 * Everybody taking bookings across the platform, in the shape the clinic's calendar rows read.
 * Public times leave out what is taken, so every time that arrives is an open one.
 */
export const bookablePeople = (trainers: BookableTrainer[]): BookablePerson[] =>
  trainers.flatMap((trainer) =>
    trainer.specialists.map((specialist) => {
      const card = trainer.availability.specialists.find((entry) => entry.specialistId === specialist.id);
      const serviceId = card?.sessionTypeId ?? specialist.serviceIds[0] ?? null;
      return {
        trainer,
        serviceName: trainer.services.find((service) => service.id === serviceId)?.name ?? null,
        serviceTypes: trainer.services.flatMap((service) =>
          specialist.serviceIds.includes(service.id) && service.serviceType ? [service.serviceType] : [],
        ),
        specialist: {
          ...specialist,
          serviceId,
          days: card
            ? card.days.map((day) => ({
                date: day.date,
                slots: day.slots.map((slot) => ({ startAt: slot.startAt, available: true })),
              }))
            : emptyDays(trainer.availability.from, trainer.availability.days),
          nextAvailable: card?.nextAvailable ?? null,
        },
      };
    }),
  );

/** The first open time on the page, or the first one after it. */
export const nextOpenTime = (specialist: PublicSpecialist) =>
  specialist.days.flatMap((day) => day.slots).find((slot) => slot.available)?.startAt ?? specialist.nextAvailable;

/**
 * Column count for a row of people, by how many there are: one, two or three fill the row
 * instead of leaving an empty seat, four or more wrap in fours.
 */
export const peopleColumns = (count: number) =>
  count >= 4 ? "lg:grid-cols-4" : count === 3 ? "lg:grid-cols-3" : count === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";

/** A person as the clinic shows a doctor: who they are, and their next open time one tap away. */
const PersonCard = ({ person, onPick }: { person: BookablePerson; onPick: (startAt: string) => void }) => {
  const { trainer, specialist } = person;
  const next = nextOpenTime(specialist);
  const when = next ? openTimeLabelIn(next, specialist.timeZone) : null;

  return (
    <article className="flex flex-col rounded-[28px] bg-card p-6 md:p-7">
      <Link
        to="/$trainerSlug"
        params={{ trainerSlug: trainer.slug }}
        className="group flex items-center gap-[18px] rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <CoachPhoto name={specialist.name} src={specialist.avatarUrl} className="size-[84px] rounded-3xl text-2xl" />
        <span className="min-w-0">
          <b className="block truncate text-2xl font-medium tracking-[-0.03em] group-hover:underline">
            {specialist.name}
          </b>
          <span className="block truncate text-muted-foreground">{specialist.title || trainer.business.name}</span>
        </span>
      </Link>

      <div className="h-6" />

      <div className="mt-auto flex items-center justify-between gap-3 rounded-[20px] bg-[var(--site-well)] py-3 pr-3 pl-[18px]">
        <div className="min-w-0">
          <span className="text-[13px] text-muted-foreground">{m.landing_team_next()}</span>
          <b className="block truncate font-semibold">{when ?? m.landing_team_full()}</b>
        </div>
        {next && when ? (
          <button
            type="button"
            aria-label={m.landing_team_book({ name: specialist.name, when })}
            className="grid size-[46px] shrink-0 place-items-center rounded-full bg-primary text-primary-foreground outline-none transition-[filter,transform] hover:brightness-105 focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]"
            onClick={() => onPick(next)}
          >
            <ArrowUpRight aria-hidden className="size-[18px]" />
          </button>
        ) : null}
      </div>
    </article>
  );
};

/** `/coaches` before anybody publishes: what the page will show, and the first step meanwhile. */
const EmptyDirectory = () => (
  <Section id="coaches">
    <SectionHead title={m.portal_coaches_title()} />
    <div className="grid gap-8 rounded-[32px] bg-[var(--site-tint)] p-6 md:grid-cols-[1fr_auto] md:items-end md:p-10">
      <div className="max-w-[520px]">
        <b className="block text-3xl font-medium tracking-[-0.03em]">{m.portal_coaches_empty_title()}</b>
        <p className="mt-3 text-lg leading-[1.55] text-muted-foreground">{m.portal_coaches_empty_body()}</p>
      </div>
      <GoLink to="/signup" tone="lime">
        {m.portal_nav_sign_up()}
      </GoLink>
    </div>
  </Section>
);

/**
 * Who is taking bookings, right now, the way the clinic template shows its doctors: a card per
 * person with their next open time, and the whole week of open times behind one button. Every
 * time is real, read without an account, and tapping one opens the same booking step a trainer's
 * own page does.
 *
 * With nobody published the page leaves the section out rather than shown empty - an empty
 * list reads as a broken platform, and the rest of the page still works for a person who already
 * has a coach. `standalone` is the directory on `/coaches`, under that page's own hero: with nobody
 * to show it says what will be here and how to start meanwhile.
 */
export const CoachDirectory = ({
  trainers,
  standalone = false,
}: {
  trainers: BookableTrainer[];
  standalone?: boolean;
}) => {
  const people = useMemo(() => bookablePeople(trainers), [trainers]);
  const [pick, setPick] = useState<SlotPick | null>(null);
  const [weekOpen, setWeekOpen] = useState(false);

  if (!people.length) return standalone ? <EmptyDirectory /> : null;

  const pickFor = (person: BookablePerson) => (tapped: Omit<SlotPick, "organizationId">) =>
    setPick({ ...tapped, organizationId: person.trainer.business.organizationId });
  const picked =
    people.find(
      (person) =>
        person.specialist.id === pick?.specialistId && person.trainer.business.organizationId === pick.organizationId,
    ) ?? null;
  const header = people[0]?.specialist.days ?? [];

  return (
    <Section id="coaches">
      <SectionHead title={m.portal_coaches_title()} aside={m.portal_coaches_aside()} />
      <div className={cn("grid gap-3 sm:grid-cols-2", peopleColumns(Math.min(people.length, VISIBLE)))}>
        {people.slice(0, VISIBLE).map((person) => (
          <PersonCard
            key={`${person.trainer.business.organizationId}:${person.specialist.id}`}
            person={person}
            onPick={(startAt) =>
              pickFor(person)({ specialistId: person.specialist.id, serviceId: person.specialist.serviceId, startAt })
            }
          />
        ))}
      </div>

      {weekOpen ? (
        <div
          className="mt-3 grid gap-2 rounded-[28px] bg-card p-3 md:p-4"
          style={{ "--days": header.length } as CSSProperties}
        >
          <div className={cn(row, "hidden items-center py-2 text-[13px] text-muted-foreground lg:grid")}>
            <span>{m.landing_calendar_count({ count: people.length })}</span>
            {header.map((day, index) => {
              const label = dayLabel(day.date, index, 0);
              return (
                <span key={day.date} className="text-center whitespace-nowrap">
                  <b
                    className={cn(
                      "mr-1.5 font-semibold text-foreground",
                      index === 0 && "rounded-full bg-primary px-2 py-0.5 text-primary-foreground",
                    )}
                  >
                    {label.number}
                  </b>
                  {label.name}
                </span>
              );
            })}
          </div>
          {people.map((person) => (
            <CoachRow
              key={`${person.trainer.business.organizationId}:${person.specialist.id}`}
              specialist={person.specialist}
              serviceName={person.serviceName}
              onPick={pickFor(person)}
            />
          ))}
        </div>
      ) : null}

      <div className="mt-4 flex justify-center">
        <button
          type="button"
          aria-expanded={weekOpen}
          className={cn(ghostButton, "h-[46px] px-6 text-sm")}
          onClick={() => setWeekOpen((open) => !open)}
        >
          {weekOpen ? m.portal_coaches_week_hide() : m.portal_coaches_week_show()}
        </button>
      </div>

      <SlotGateDialog
        pick={pick}
        specialist={picked?.specialist ?? null}
        serviceName={picked?.serviceName ?? null}
        onClose={() => setPick(null)}
      />
    </Section>
  );
};
